import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { KeyRound, Pencil, Power, RefreshCw, ShieldAlert, Unlock, UserPlus } from "lucide-react";
import { ApiError } from "@/api/client";
import { usersApi, type InternalRole, type LoginLockout, type UserAccount } from "@/api/userClient";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Modal } from "@/components/ui/modal";
import { TableShell, type ListParams } from "@/components/TableShell";
import { useAuthStore } from "@/stores/auth";
import { useTranslation } from "@/framework-originated/i18n";

const BASE_ROLES: InternalRole[] = ["admin", "operator", "finance"];

function roleLabel(role: string): string {
  switch (role) {
    case "sys":
      return "系统";
    case "admin":
      return "管理员";
    case "finance":
      return "财务";
    default:
      return "运营";
  }
}

function fmtTime(v: string | null | undefined): string {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleString();
}

const emptyCreate = {
  username: "",
  password: "",
  role: "operator" as InternalRole,
  enabled: true,
};

export function UsersPage() {
  const { t } = useTranslation("users");
  const qc = useQueryClient();
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const isSys = currentUser?.role === "sys";
  const canManage = currentUser?.role === "admin" || isSys;
  const roles: InternalRole[] = isSys ? ["sys", ...BASE_ROLES] : BASE_ROLES;

  const [params, setParams] = useState<ListParams>({ page: 1, page_size: 30 });
  const [roleFilter, setRoleFilter] = useState<InternalRole | "">("");
  const [enabledFilter, setEnabledFilter] = useState<"" | "true" | "false">("");
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [editing, setEditing] = useState<UserAccount | null>(null);
  const [editForm, setEditForm] = useState<{ role: InternalRole; enabled: boolean }>({ role: "operator", enabled: true });
  const [resetting, setResetting] = useState<UserAccount | null>(null);
  const [resetPassword, setResetPassword] = useState("");

  const listQ = useQuery({
    queryKey: ["users", params, roleFilter, enabledFilter],
    queryFn: () =>
      usersApi.list({
        q: params.q,
        page: params.page,
        page_size: params.page_size,
        role: roleFilter || undefined,
        enabled: enabledFilter === "" ? undefined : enabledFilter === "true",
      }),
    enabled: canManage,
  });

  const lockoutsQ = useQuery({
    queryKey: ["user-lockouts"],
    queryFn: usersApi.listLockouts,
    enabled: canManage,
    refetchInterval: 15_000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["users"] });
    qc.invalidateQueries({ queryKey: ["user-lockouts"] });
  };

  const createMut = useMutation({
    mutationFn: () =>
      usersApi.create({
        username: createForm.username.trim(),
        password: createForm.password,
        role: createForm.role,
        enabled: createForm.enabled,
      }),
    onSuccess: (u) => {
      invalidate();
      setCreating(false);
      setCreateForm(emptyCreate);
      setErr(null);
      setMsg(t("created", { username: u.username }));
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });

  const updateMut = useMutation({
    mutationFn: () =>
      usersApi.update(editing!.id, { role: editForm.role, enabled: editForm.enabled }),
    onSuccess: () => {
      invalidate();
      setEditing(null);
      setErr(null);
      setMsg(t("updated"));
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });

  const toggleMut = useMutation({
    mutationFn: (u: UserAccount) => usersApi.update(u.id, { enabled: !u.enabled }),
    onSuccess: (_data, u) => {
      invalidate();
      setMsg(u.enabled ? t("disabled", { username: u.username }) : t("enabled", { username: u.username }));
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });

  const resetMut = useMutation({
    mutationFn: () => usersApi.resetPassword(resetting!.id, resetPassword),
    onSuccess: () => {
      const isSelf = resetting?.id === currentUser?.id;
      invalidate();
      setResetting(null);
      setResetPassword("");
      setErr(null);
      setMsg(t("resetDone"));
      if (isSelf) {
        logout();
        navigate({ to: "/login", replace: true });
      }
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });

  const unlockMut = useMutation({
    mutationFn: (username: string) => usersApi.unlock(username),
    onSuccess: (_d, username) => {
      invalidate();
      setMsg(t("unlocked", { username }));
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });

  if (!canManage) {
    return (
      <div className="rounded-xl border bg-card p-6 text-sm text-muted-foreground">
        {t("adminOnly")}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t("title")}</h1>
          <p className="mt-0.5 text-xs text-muted-foreground">{t("description")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { listQ.refetch(); lockoutsQ.refetch(); }}>
            <RefreshCw className={`mr-1 h-3.5 w-3.5 ${listQ.isFetching || lockoutsQ.isFetching ? "animate-spin" : ""}`} />
            {t("refresh")}
          </Button>
          <Button size="sm" onClick={() => { setCreating(true); setErr(null); setMsg(null); }}>
            <UserPlus className="mr-1 h-4 w-4" /> {t("create")}
          </Button>
        </div>
      </div>

      {msg && <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">{msg}</div>}
      {err && <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">{err}</div>}

      <div className="rounded-xl border bg-card p-3 shadow-sm">
        <TableShell
          key={`${roleFilter}|${enabledFilter}`}
          mode="server"
          columns={[
            { key: "id", label: "ID" },
            { key: "username", label: t("username") },
            { key: "role", label: t("role") },
            { key: "enabled", label: t("status") },
            { key: "last_login_at", label: t("lastLogin") },
            { key: "created_at", label: t("createdAt") },
            { key: "actions", label: "", align: "right" },
          ]}
          data={listQ.data?.items}
          total={listQ.data?.total}
          loading={listQ.isLoading}
          defaultPageSize={30}
          pageSizeOptions={[10, 20, 30, 50, 100]}
          onParamsChange={setParams}
          emptyText={t("empty")}
          search={{ placeholder: t("searchPlaceholder") }}
          filters={
            <div className="flex items-center gap-2">
              <select
                className="h-9 rounded-md border bg-background px-2 text-sm"
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value as InternalRole | "");
                  setParams({ page: 1, page_size: 30 });
                }}
              >
                <option value="">{t("allRoles")}</option>
                {roles.map((r) => (
                  <option key={r} value={r}>{roleLabel(r)}</option>
                ))}
              </select>
              <select
                className="h-9 rounded-md border bg-background px-2 text-sm"
                value={enabledFilter}
                onChange={(e) => {
                  setEnabledFilter(e.target.value as "" | "true" | "false");
                  setParams({ page: 1, page_size: 30 });
                }}
              >
                <option value="">{t("allStatus")}</option>
                <option value="true">{t("statusEnabled")}</option>
                <option value="false">{t("statusDisabled")}</option>
              </select>
            </div>
          }
          rowKey={(u) => u.id}
          renderRow={(u) => {
            const isSelf = u.id === currentUser?.id;
            return (
              <tr className="border-b hover:bg-muted/30">
                <td className="py-2 text-xs text-muted-foreground">{u.id}</td>
                <td className="font-medium">
                  {u.username}
                  {isSelf && <span className="ml-1.5 text-[10px] text-muted-foreground">({t("self")})</span>}
                </td>
                <td><Badge variant={u.role === "sys" ? "warning" : u.role === "admin" ? "default" : "muted"}>{roleLabel(u.role)}</Badge></td>
                <td>
                  <Badge variant={u.enabled ? "success" : "destructive"}>
                    {u.enabled ? t("statusEnabled") : t("statusDisabled")}
                  </Badge>
                </td>
                <td className="text-xs text-muted-foreground">{fmtTime(u.last_login_at)}</td>
                <td className="text-xs text-muted-foreground">{fmtTime(u.created_at)}</td>
                <td className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      title={t("edit")}
                      onClick={() => {
                        setEditing(u);
                        setEditForm({ role: u.role, enabled: u.enabled });
                        setErr(null);
                        setMsg(null);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      title={u.enabled ? t("disable") : t("enable")}
                      disabled={isSelf || toggleMut.isPending}
                      onClick={() => {
                        if (u.enabled && !window.confirm(t("disableConfirm", { username: u.username }))) return;
                        toggleMut.mutate(u);
                      }}
                    >
                      <Power className={`h-3.5 w-3.5 ${u.enabled ? "text-destructive" : "text-green-600"}`} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      title={t("resetPassword")}
                      onClick={() => {
                        setResetting(u);
                        setResetPassword("");
                        setErr(null);
                        setMsg(null);
                      }}
                    >
                      <KeyRound className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            );
          }}
        />
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="flex items-center gap-1.5 text-sm font-semibold">
                <ShieldAlert className="h-4 w-4" /> {t("lockouts.title")}
              </h2>
              <p className="mt-0.5 text-xs text-muted-foreground">{t("lockouts.description")}</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => lockoutsQ.refetch()}>
              <RefreshCw className={`mr-1 h-3.5 w-3.5 ${lockoutsQ.isFetching ? "animate-spin" : ""}`} />
              {t("refresh")}
            </Button>
          </div>
          {lockoutsQ.isLoading ? (
            <div className="py-4 text-sm text-muted-foreground">…</div>
          ) : (lockoutsQ.data?.items.length ?? 0) === 0 ? (
            <div className="py-4 text-center text-sm text-muted-foreground">{t("lockouts.empty")}</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b text-left text-muted-foreground">
                <tr>
                  <th className="py-2">{t("username")}</th>
                  <th>{t("lockouts.fails")}</th>
                  <th>{t("lockouts.remaining")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {lockoutsQ.data!.items.map((l: LoginLockout) => (
                  <tr key={l.username} className="border-b">
                    <td className="py-2 font-medium">{l.username}</td>
                    <td className="text-xs">{l.fails}</td>
                    <td className="text-xs text-muted-foreground">
                      {t("lockouts.minutes", { count: Math.max(1, Math.ceil(l.remaining_seconds / 60)) })}
                    </td>
                    <td className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={unlockMut.isPending}
                        onClick={() => unlockMut.mutate(l.username)}
                      >
                        <Unlock className="mr-1 h-3.5 w-3.5" /> {t("lockouts.unlock")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {creating && (
        <Modal title={t("createTitle")} subtitle={t("createHint")} onClose={() => { setCreating(false); setErr(null); }}>
          <form
            className="grid grid-cols-2 gap-3"
            onSubmit={(e) => { e.preventDefault(); setErr(null); createMut.mutate(); }}
          >
            <div className="space-y-1">
              <Label>{t("username")} *</Label>
              <Input
                value={createForm.username}
                onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
                required
                maxLength={50}
                autoComplete="off"
              />
            </div>
            <div className="space-y-1">
              <Label>{t("password")} *</Label>
              <Input
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                required
                minLength={8}
                maxLength={128}
                autoComplete="new-password"
              />
              <p className="text-[11px] text-muted-foreground">{t("passwordHint")}</p>
            </div>
            <div className="space-y-1">
              <Label>{t("role")}</Label>
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={createForm.role}
                onChange={(e) => setCreateForm({ ...createForm, role: e.target.value as InternalRole })}
              >
                {roles.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label>{t("status")}</Label>
              <label className="flex h-10 items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={createForm.enabled}
                  onChange={(e) => setCreateForm({ ...createForm, enabled: e.target.checked })}
                  className="h-4 w-4 rounded border"
                />
                {createForm.enabled ? t("statusEnabled") : t("statusDisabled")}
              </label>
            </div>
            {err && <div className="col-span-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">{err}</div>}
            <div className="col-span-2 flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => { setCreating(false); setErr(null); }}>{t("cancel")}</Button>
              <Button type="submit" disabled={createMut.isPending}>{createMut.isPending ? "…" : t("save")}</Button>
            </div>
          </form>
        </Modal>
      )}

      {editing && (
        <Modal title={t("editTitle", { username: editing.username })} onClose={() => { setEditing(null); setErr(null); }}>
          <form
            className="space-y-3"
            onSubmit={(e) => { e.preventDefault(); setErr(null); updateMut.mutate(); }}
          >
            {editing.id === currentUser?.id && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {t("selfProtection")}
              </div>
            )}
            <div className="space-y-1">
              <Label>{t("role")}</Label>
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={editForm.role}
                disabled={editing.id === currentUser?.id}
                onChange={(e) => setEditForm({ ...editForm, role: e.target.value as InternalRole })}
              >
                {roles.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label>{t("status")}</Label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={editForm.enabled}
                  disabled={editing.id === currentUser?.id}
                  onChange={(e) => setEditForm({ ...editForm, enabled: e.target.checked })}
                  className="h-4 w-4 rounded border"
                />
                {editForm.enabled ? t("statusEnabled") : t("statusDisabled")}
              </label>
            </div>
            {err && <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">{err}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => { setEditing(null); setErr(null); }}>{t("cancel")}</Button>
              <Button type="submit" disabled={updateMut.isPending}>{updateMut.isPending ? "…" : t("save")}</Button>
            </div>
          </form>
        </Modal>
      )}

      {resetting && (
        <Modal title={t("resetTitle", { username: resetting.username })} subtitle={t("resetHint")} onClose={() => { setResetting(null); setErr(null); }}>
          <form
            className="space-y-3"
            onSubmit={(e) => { e.preventDefault(); setErr(null); resetMut.mutate(); }}
          >
            <div className="space-y-1">
              <Label>{t("newPassword")} *</Label>
              <Input
                type="password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                required
                minLength={8}
                maxLength={128}
                autoComplete="new-password"
              />
            </div>
            {err && <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">{err}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => { setResetting(null); setErr(null); }}>{t("cancel")}</Button>
              <Button type="submit" disabled={resetMut.isPending}>{resetMut.isPending ? "…" : t("resetPassword")}</Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
